import { sleep } from './common';

export interface PerformWithRetriesOptions {
  retryCount?: number;
  retryDelayMs?: number;
  exponentialBackoff?: boolean;
  currentAttempt?: number;
  /**
   * Return false to rethrow the error immediately instead of retrying.
   * Defaults to retrying every error.
   */
  shouldRetry?: (error: unknown) => boolean;
  /** Called with the failure before each retry delay (not on terminal failure). */
  onRetry?: (error: unknown) => void;
}

export const performWithRetries = async <T>(
  functionToExecute: () => Promise<T>,
  options: PerformWithRetriesOptions = {},
): Promise<T> => {
  const {
    retryCount = 3,
    retryDelayMs = 1000,
    exponentialBackoff = false,
    currentAttempt = 0,
    shouldRetry,
    onRetry,
  } = options;
  try {
    const result = await functionToExecute();
    return result;
  } catch (e) {
    const newRetryCount = retryCount - 1;
    if (newRetryCount <= 0 || shouldRetry?.(e) === false) {
      throw e;
    }

    onRetry?.(e);

    const delay = exponentialBackoff
      ? retryDelayMs * Math.pow(2, currentAttempt)
      : retryDelayMs;

    await sleep(delay);
    return performWithRetries(functionToExecute, {
      retryCount: newRetryCount,
      retryDelayMs,
      exponentialBackoff,
      currentAttempt: currentAttempt + 1,
      shouldRetry,
      onRetry,
    });
  }
};
